import { AuthError, InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';
import { computed, ref } from 'vue';

const token = ref<string | null>(null);
const account = ref<AccountInfo | null>(null);

export const useEntraAuth = () => {
  const { $msal } = useNuxtApp();
  const scopes = ['User.Read'];

  const syncAccount = () => {
    const activeAccount = $msal.getActiveAccount();
    const resolved = activeAccount ?? $msal.getAllAccounts()[0] ?? null;
    if (resolved && !activeAccount) {
      $msal.setActiveAccount(resolved);
    }
    account.value = resolved;
  };

  const acquireToken = async (): Promise<string | null> => {
    syncAccount();
    if (!account.value) {
      token.value = null;
      return null;
    }

    try {
      const result = await $msal.acquireTokenSilent({
        account: account.value,
        scopes,
      });
      token.value = result.idToken;
      return result.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        token.value = null;
        await logout();
        return null;
      }
      if (error instanceof AuthError) {
        console.error('[auth] acquireToken failed:', {
          errorCode: error.errorCode,
          errorMessage: error.errorMessage,
          correlationId: error.correlationId,
        });
      } else {
        console.error('[auth] acquireToken failed:', error);
      }
      throw error;
    }
  };

  const login = async () => {
    await $msal.loginRedirect({ scopes });
  };

  const logout = async () => {
    token.value = null;
    account.value = null;
    await $msal.clearCache();
    await navigateTo('/auth/login');
  };

  const isAuthenticated = computed(() => account.value !== null);

  const userName = computed<string>(() => account.value?.name ?? account.value?.username ?? '');

  syncAccount();

  return {
    token,
    userName,
    isAuthenticated,
    login,
    logout,
    acquireToken,
  };
};
